import { ReactNode, useEffect, useState } from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import { Constants } from '@arrived/common';
import { Book } from '@arrived/images';

import { useMediaQueryClasses } from '../../hooks';

interface PropertyDetailsTipsProps {
  title: ReactNode;
  tipText: ReactNode;
  isLearningMode?: boolean;
}

const StyledPropertyDetailsTips = styled(Accordion)`
  &.property-details-tips {
    padding: ${(props) => props.theme.spacing(2.5, 4)};
    margin-top: ${(props) => props.theme.spacing(0)};
    background-color: ${({ theme }) => theme.palette.neutrals.milk};

    &.Mui-expanded {
      &:first-of-type {
        margin-top: ${(props) => props.theme.spacing(0)};
      }

      .property-details-tips-title-wrapper {
        padding-bottom: ${(props) => props.theme.spacing(0)};
      }
    }

    .property-details-tips-title-wrapper {
      flex-direction: row-reverse;
      align-items: center;
      min-height: auto;
      padding: ${(props) => props.theme.spacing(0)};

      .MuiAccordionSummary-content {
        margin: ${(props) => props.theme.spacing(0, 0, 0, 2)};
      }

      .MuiAccordionSummary-expandIconWrapper {
        align-self: center;

        > svg path {
          fill: ${({ theme }) => theme.palette.darkened.steel};
          opacity: 1;
        }

        &.Mui-expanded {
          transform: none;

          > svg path {
            fill: ${({ theme }) => theme.palette.tertiary[500]};
          }
        }
      }
    }

    .property-details-tips-text-wrapper {
      max-width: 90ch;
      padding: ${(props) => props.theme.spacing(0)};

      .property-details-tips-text {
        padding-top: ${(props) => props.theme.spacing(1)};
      }
    }

    &.mobile {
      padding: ${(props) => props.theme.spacing(2.5, 2.5)};
    }
  }
`;

export const PropertyDetailsTips = (props: PropertyDetailsTipsProps) => {
  const { title, tipText, isLearningMode } = props;
  const [isTipExpanded, setIsTipExpanded] = useState(true);
  const mediaQueryClasses = useMediaQueryClasses();

  useEffect(() => {
    setIsTipExpanded(Boolean(isLearningMode));
  }, [isLearningMode]);

  return (
    <StyledPropertyDetailsTips
      elevation={0}
      expanded={isTipExpanded}
      onChange={(event) => {
        event.preventDefault();
        setIsTipExpanded(!isTipExpanded);
      }}
      className={clsx('property-details-tips', mediaQueryClasses)}
    >
      <AccordionSummary expandIcon={<Book />} className="property-details-tips-title-wrapper">
        <Typography variant="h5.semibold" className="property-details-tips-title">
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className="property-details-tips-text-wrapper">
        <Typography variant="body1" className="property-details-tips-text">
          {tipText}
        </Typography>
      </AccordionDetails>
    </StyledPropertyDetailsTips>
  );
};
